import { Fragment, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Citation } from "../api/client";
import CitationChip from "./Citation";

interface Props {
  content: string;
  citations: Citation[];
}

const CITATION_RE = /\[#(\d+)\]/g;

/** Render assistant markdown with `[#<source_id>]` markers turned into citation chips. */
export default function ChatStream({ content, citations }: Props) {
  // Replace inline citation markers with placeholders that survive markdown parsing.
  const counter = new Map<number, number>();
  const placeholders: { id: number; sourceId: number }[] = [];
  const transformed = content.replace(CITATION_RE, (_m, sid: string) => {
    const sourceId = Number(sid);
    if (!counter.has(sourceId)) counter.set(sourceId, counter.size + 1);
    placeholders.push({ id: placeholders.length, sourceId });
    return `⁣CITE${placeholders.length - 1}⁣`;
  });

  function splitWithChips(text: string): ReactNode[] {
    const out: ReactNode[] = [];
    const re = /⁣CITE(\d+)⁣/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const placeholderIdx = Number(m[1]);
      const ph = placeholders[placeholderIdx];
      const idx = (counter.get(ph.sourceId) ?? 0);
      out.push(
        <CitationChip
          key={`cite-${placeholderIdx}`}
          sourceId={ph.sourceId}
          index={idx}
          citations={citations}
        />,
      );
      last = re.lastIndex;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p>{transformChildren(children)}</p>,
        li: ({ children }) => <li>{transformChildren(children)}</li>,
        td: ({ children }) => <td>{transformChildren(children)}</td>,
        th: ({ children }) => <th>{transformChildren(children)}</th>,
      }}
    >
      {transformed}
    </ReactMarkdown>
  );

  function transformChildren(children: ReactNode): ReactNode[] {
    const arr = Array.isArray(children) ? children : [children];
    return arr.flatMap((child, i) => {
      if (typeof child === "string") {
        return splitWithChips(child).map((node, j) => <Fragment key={`${i}-${j}`}>{node}</Fragment>);
      }
      return [<Fragment key={i}>{child}</Fragment>];
    });
  }
}
